/**
 * EW-9 inventory pieces shared by the list, the item page and the daily log.
 * Every figure is the API's (on hand, stock value, a move's total) — nothing
 * here sums or prices anything. An absent figure renders `—`.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { fmtNumber, type EwSkin } from '../../../lib/earthworks';

/** `38 m³`, `-2 each`; absent → `—`. */
export function ewQty(n: number | null | undefined, unitLabel: string | null | undefined): string {
  const v = fmtNumber(n);
  if (v === '—') return v;
  return unitLabel ? `${v} ${unitLabel}` : v;
}

/** A signed move quantity: `+180 m³`, `−25 m³`. */
export function ewSignedQty(n: number | null | undefined, unitLabel: string | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const body = ewQty(Math.abs(n), unitLabel);
  return n > 0 ? `+${body}` : n < 0 ? `−${body}` : body;
}

export const EW_STOCK_SKIN: Record<'ok' | 'low' | 'negative', EwSkin> = {
  ok: { label: 'In stock', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  low: { label: 'Low stock', fill: 'bg-fi-amber', rail: 'border-l-fi-amber' },
  negative: { label: 'Below zero', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export const stockState = (m: { lowStock: boolean; negative: boolean }) => (m.negative ? 'negative' : m.lowStock ? 'low' : 'ok');

/** Low / below-zero stock, in amber. A warning only: whatever caused it was saved. */
export function EwStockWarnings({ messages, testId = 'ew-stock-warnings', lead }: { messages: string[]; testId?: string; lead?: string }) {
  if (messages.length === 0) return null;
  return (
    <div role="status" data-testid={testId} className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
      <div>
        {lead && <p className="font-semibold text-white">{lead}</p>}
        <ul className="space-y-1">
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
